import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import WarningAmberIcon from "@mui/icons-material/Warning"

import MapInfoCard from "@/components/map/MapInfoCard.jsx"
import VesselCardContent from "@/features/vessel/VesselCardContent.jsx"
import WaypointCardBody from "@/features/route/WaypointCardBody.jsx"
import WaypointDeleteAction from "@/features/route/WaypointDeleteAction.jsx"
import TransitCardBody from "@/features/route/TransitCardBody.jsx"
import LegCardBody from "@/features/route/LegCardBody.jsx"
import FeatureCardBody from "@/features/map/FeatureCardBody.jsx"
import { ORANGE, RED } from "@/features/route/alertCardParts.jsx"
import { findCardColor, waypointLabel } from "@/utils/routeHelper.jsx"
import { useIsCalculating } from "@/features/route/state/RouteStore.jsx"

// Three weather tiles with label and direction arrow need this width.
const WAYPOINT_CARD_WIDTH = 300

// Rendert die Info-Karte (Vessel, Waypoint, Transit, Leg-Warnung oder GIS-Info) zum aktiven Klickziel.
export default function ActiveInfoCard({ activeCard, onClose }) {
  const isCalculating = useIsCalculating()
  if (!activeCard?.data) return null

  if (activeCard.type === "vessel") {
    const vesselRouteType =
      activeCard.data?.routeType ??
      activeCard.data?.geojson?.features?.[0]?.properties?.routeType
    const vesselState =
      activeCard.data?.state ??
      activeCard.data?.geojson?.features?.[0]?.properties?.state
    const color = findCardColor(vesselRouteType, vesselState)
    return (
      <MapInfoCard
        anchorLatLng={activeCard.anchor}
        onClose={onClose}
        title={activeCard.data?.vesselName ?? "Vessel"}
        icon={<DirectionsBoatIcon sx={{ color, fontSize: 16 }} />}
        headerColor={color}
      >
        <VesselCardContent selectedVessel={activeCard.data} color={color} />
      </MapInfoCard>
    )
  }

  if (activeCard.type === "waypoint") {
    const edit = activeCard.edit
    const color = findCardColor(
      activeCard.routeType,
      activeCard.state ?? activeCard.data?.properties?.state,
    )
    return (
      <MapInfoCard
        anchorLatLng={activeCard.anchor}
        onClose={onClose}
        title={
          activeCard.vesselName ??
          activeCard.data?.properties?.vesselName ??
          activeCard.data?.properties?.routeName ??
          "Waypoint"
        }
        subtitle={activeCard.label ?? edit?.label ?? waypointLabel(activeCard.data?.properties)}
        icon={<LocationOnIcon sx={{ color, fontSize: 16 }} />}
        headerColor={color}
        width={WAYPOINT_CARD_WIDTH}
        actions={
          edit?.onDelete ? (
            <WaypointDeleteAction
              onDelete={edit.onDelete}
              label={edit.deleteLabel}
              disabled={isCalculating}
            />
          ) : null
        }
      >
        <WaypointCardBody
          feature={activeCard.data}
          routeType={activeCard.routeType}
          sketch={edit?.sketch}
          edit={edit ? { ...edit, disabled: isCalculating } : null}
          limits={activeCard.limits}
          color={color}
        />
      </MapInfoCard>
    )
  }

  if (activeCard.type === "alert") {
    const isLeg = activeCard.data.kind === "leg"
    const items = (isLeg ? activeCard.data.issues : activeCard.data.sections) ?? []
    const warned = isLeg
      ? items.some((i) => i.level === "warning")
      : items.some((s) => s.zoneKind !== "eca")
    const only = items[0]
    const typeLabel = isLeg
      ? only?.title
      : only?.type === "weather"
        ? only.parameter.charAt(0).toUpperCase() + only.parameter.slice(1)
        : only?.zoneKind === "eca"
          ? "ECA"
          : "Zone"
    const prefix = isLeg
      ? `Leg ${activeCard.data.from?.leg ?? ""} → ${activeCard.data.to?.leg ?? ""}`
      : "Transit"
    return (
      <MapInfoCard
        anchorLatLng={activeCard.anchor}
        onClose={onClose}
        title={activeCard.vesselName ?? "Route"}
        subtitle={`${prefix} · ${items.length > 1 ? `${items.length} issues` : typeLabel}`}
        icon={
          <WarningAmberIcon
            sx={{ color: warned ? RED.color : ORANGE.color, fontSize: 16 }}
          />
        }
        headerColor={findCardColor(activeCard.routeType, activeCard.state)}
      >
        {isLeg ? <LegCardBody {...activeCard.data} /> : <TransitCardBody {...activeCard.data} />}
      </MapInfoCard>
    )
  }

  if (activeCard.type === "info") {
    const { title, subtitle, icon, rows, text, footer } = activeCard.data
    return (
      <MapInfoCard
        anchorLatLng={activeCard.anchor}
        onClose={onClose}
        title={title}
        subtitle={subtitle}
        icon={icon}
      >
        <FeatureCardBody rows={rows} text={text} footer={footer} />
      </MapInfoCard>
    )
  }

  return null
}
